import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import api from "../services/api";
import { useUser } from "../context/UserContext";
import { formatWorkContextRole, getWorkContextRoleNames } from "../utils/workContext";

function fallbackTenantLabel(tenantId, isPlatformContext) {
  if (isPlatformContext) return "Logboek24 platform";
  return String(tenantId || "").trim() ? "Tenantnaam niet beschikbaar" : "Geen tenantcontext";
}

function getUserLabel(user) {
  return String(user?.naam || user?.name || user?.email || "").trim();
}

export default function WorkContextBadge() {
  const { user } = useUser();
  const location = useLocation();
  const [tenantName, setTenantName] = useState("");

  const roles = useMemo(() => getWorkContextRoleNames(user), [user]);
  const normalizedRoles = useMemo(
    () => roles.map((role) => role.toLowerCase()),
    [roles]
  );
  const accountType = String(user?.accountType || "internal").trim().toLowerCase();
  const isCustomerPortal = accountType === "customer_portal";
  const isRootContext = Boolean(user?.isRoot || user?.root) || normalizedRoles.includes("root");
  const isPlatformContext = isRootContext || normalizedRoles.includes("platformadmin");
  const primaryRole = isCustomerPortal
    ? "Klantportaal"
    : formatWorkContextRole(roles[0] || (isPlatformContext ? "PlatformAdmin" : "Gebruiker"));
  const visibleRoles = isCustomerPortal ? [] : roles.map(formatWorkContextRole);
  const userLabel = getUserLabel(user);
  const pathname = String(location.pathname || "");

  useEffect(() => {
    let active = true;
    if (!user?.tenantId || isPlatformContext) {
      setTenantName("");
      return undefined;
    }

    // This is the existing tenant identity source; this component only presents its result.
    api
      .get("/tenant/settings/monteur-menu")
      .then((res) => {
        if (active) setTenantName(String(res?.data?.tenant?.companyName || "").trim());
      })
      .catch(() => {
        if (active) setTenantName("");
      });

    return () => {
      active = false;
    };
  }, [isPlatformContext, user?.tenantId]);

  if (!user || pathname === "/login" || pathname.startsWith("/wachtwoord-") || pathname === "/reset-wachtwoord") {
    return null;
  }

  const tenantLabel = tenantName || fallbackTenantLabel(user?.tenantId, isPlatformContext);
  const contextWarning = isRootContext
    ? "Je werkt met verhoogde rootrechten."
    : isPlatformContext
      ? "Je werkt momenteel in platformbeheer."
      : "";

  return (
    <aside
      aria-label="Werkcontext"
      className={`fixed bottom-3 right-3 z-[70] w-[min(22rem,calc(100vw-1.5rem))] rounded-xl border px-3 py-2.5 shadow-lg backdrop-blur ${
        contextWarning
          ? "border-amber-300 bg-amber-50/95"
          : "border-sky-200 bg-white/95"
      }`}
    >
      <div className="flex min-w-0 items-center gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Werkcontext</p>
          <p className="truncate text-sm font-semibold text-slate-900" title={tenantLabel}>{tenantLabel}</p>
        </div>
        <span
          className="max-w-[9rem] truncate rounded-full bg-slate-100 px-2 py-1 text-xs font-medium text-slate-700"
          title={`Rol: ${primaryRole}`}
          aria-label={`Rol: ${primaryRole}`}
        >
          Rol: {primaryRole}
        </span>
      </div>

      {contextWarning ? (
        <p className="mt-2 rounded-lg bg-amber-100 px-2 py-1.5 text-xs font-medium text-amber-900">{contextWarning}</p>
      ) : null}

      <details className="mt-2 border-t border-slate-200 pt-2">
        <summary className="cursor-pointer text-xs font-medium text-slate-600 hover:text-slate-900">Meer context</summary>
        <dl className="mt-2 space-y-1.5 text-xs text-slate-600">
          {userLabel ? <ContextRow label="Gebruiker" value={userLabel} /> : null}
          {user?.email && user?.email !== userLabel ? <ContextRow label="E-mail" value={user.email} /> : null}
          {visibleRoles.length > 1 ? <ContextRow label="Overige rollen" value={visibleRoles.slice(1).join(", ")} /> : null}
          {!isCustomerPortal && user?.tenantId ? <ContextRow label="Tenant-ID" value={user.tenantId} mono /> : null}
          {isPlatformContext ? <ContextRow label="Modus" value={isRootContext ? "Root" : "Platformbeheer"} /> : null}
        </dl>
      </details>
    </aside>
  );
}

function ContextRow({ label, value, mono = false }) {
  const text = String(value || "").trim();
  if (!text) return null;
  return (
    <div className="flex min-w-0 items-start justify-between gap-3">
      <dt className="shrink-0 text-slate-500">{label}</dt>
      <dd className={`min-w-0 break-words text-right text-slate-800 ${mono ? "font-mono text-[11px]" : ""}`}>{text}</dd>
    </div>
  );
}
